import { useState, useRef, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axiosInstance from "../api/axiosInstance";
import Select from "react-select";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function ReportModal({ report, onClose, readOnly }) {
  // Existing fields
  const [comment, setComment] = useState(report.comment || "");
  const [medicines, setMedicines] = useState(report.medicines || []);
  const [followUpDate, setFollowUpDate] = useState(report.followUpDate || "");
  const [requestForDiagnosis, setRequestForDiagnosis] = useState(report.requestForDiagnosis || "");

  // New fields from CreateReportModal
  const [patientName, setPatientName] = useState(report.patientName || "");
  const [age, setAge] = useState(report.age || "");
  const [gender, setGender] = useState(report.gender || "");
  const [occupation, setOccupation] = useState(report.occupation || "");
  const [height, setHeight] = useState(report.height || "");
  const [weight, setWeight] = useState(report.weight || "");

  const [chiefComplaints, setChiefComplaints] = useState(report.chiefComplaints || "");
  const [associatedSymptom, setAssociatedSymptom] = useState(report.associatedSymptom || {
    region: "",
    inspection: "",
    palpation: "",
    rangeOfMotion: "",
    muscleStrength: "",
    specialTests: "",
  });
  const [drugAllergy, setDrugAllergy] = useState(report.drugAllergy || "");
  const [advice, setAdvice] = useState(report.advice || "");
  const [exerciseNotes, setExerciseNotes] = useState(report.exerciseNotes || "");

  const [routes, setRoutes] = useState([]);

  const pdfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getRoutes = async () => {
      try {
        const res = await axiosInstance.get("/reports/routes");
        setRoutes(res.data);
      } catch (err) {
        console.error("Failed to fetch routes", err);
      }
    };
    getRoutes();
  }, []);

  const toggle = (idx, time, field) => {
    if (readOnly) return;
    const copy = [...medicines];
    if (!copy[idx][time]) {
      copy[idx][time] = { take: false, beforeFood: false, afterFood: false };
    }
    copy[idx][time][field] = !copy[idx][time][field];

    // If unticking 'take', also reset before/after food
    if (field === 'take' && !copy[idx][time].take) {
      copy[idx][time].beforeFood = false;
      copy[idx][time].afterFood = false;
    }
    setMedicines(copy);
  };

  const save = async () => {
    await axiosInstance.put(`/reports/update/${report.id}`, {
      patientName,
      age: Number(age),
      gender,
      occupation,
      height,
      weight,
      chiefComplaints,
      associatedSymptom,
      drugAllergy,
      comment,
      medicines,
      advice,
      exerciseNotes,
      requestForDiagnosis,
      followUpDate,
    });
    onClose();
    // window.location.reload();
  };

  const downloadPdf = async () => {
    const container = pdfRef.current as HTMLDivElement | null;
    if (!container) return;

    // Temporarily make it visible for capture
    // Position fixed at top-left but behind z-index so user doesn't see it flickering over modal
    container.style.position = "fixed";
    container.style.left = "0px";
    container.style.top = "0px";
    container.style.zIndex = "-50";

    try {
      const page1 = container.querySelector("#report-page-1") as HTMLElement;
      const page2 = container.querySelector("#report-page-2") as HTMLElement;

      if (!page1 || !page2) {
        console.error("Pages not found");
        return;
      }

      // Generate PDF
      // A4 size: 210mm x 297mm
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Capture Page 1
      const canvas1 = await html2canvas(page1, { scale: 2, useCORS: true });
      const imgData1 = canvas1.toDataURL("image/png");

      // Calculate height to maintain aspect ratio, though we expect it to fit A4
      const imgProps1 = pdf.getImageProperties(imgData1);
      const p1Height = (imgProps1.height * pdfWidth) / imgProps1.width;

      pdf.addImage(imgData1, "PNG", 0, 0, pdfWidth, p1Height);

      // Capture Page 2
      pdf.addPage();
      const canvas2 = await html2canvas(page2, { scale: 2, useCORS: true });
      const imgData2 = canvas2.toDataURL("image/png");

      const imgProps2 = pdf.getImageProperties(imgData2);
      const p2Height = (imgProps2.height * pdfWidth) / imgProps2.width;

      pdf.addImage(imgData2, "PNG", 0, 0, pdfWidth, p2Height);

      pdf.save(`report-${report.id}.pdf`);
    } catch (error) {
      console.error("PDF Generation failed", error);
    } finally {
      // Hide again
      container.style.left = "-9999px";
      container.style.position = "absolute";
      container.style.zIndex = "auto";
    }
  };

  const addMedicine = () => {
    if (readOnly) return;
    setMedicines([
      ...medicines,
      {
        name: "",
        days: 1,
        dosage: "",
        route: "",
        morning: { take: false, beforeFood: false, afterFood: false },
        afternoon: { take: false, beforeFood: false, afterFood: false },
        evening: { take: false, beforeFood: false, afterFood: false },
        night: { take: false, beforeFood: false, afterFood: false },
      },
    ]);
  };

  const deleteMedicine = (index: number) => {
    if (readOnly) return;
    const copy = [...medicines];
    copy.splice(index, 1);
    setMedicines(copy);
  };

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Others", label: "Others" },
  ];

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold mb-3">{readOnly ? "View Report" : "Edit Report"}</h2>
          <button
            onClick={onClose}
            className="text-black hover:text-black-200 transition"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Demographics */}
        <h3 className="font-semibold mt-2 mb-2">Patient Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Patient Name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            readOnly={readOnly}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Age"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            readOnly={readOnly}
          />
          {readOnly ? (
            <input type="text" className="border p-2 rounded" value={gender} readOnly />
          ) : (
            <Select
              options={genderOptions}
              placeholder="Select Gender"
              value={genderOptions.find((o) => o.value === gender)}
              onChange={(e) => setGender(e?.value || "")}
            />
          )}

          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Height"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            readOnly={readOnly}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Weight"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            readOnly={readOnly}
          />
          <input
            type="text"
            className="border p-2 rounded"
            placeholder="Occupation"
            value={occupation}
            onChange={(e) => setOccupation(e.target.value)}
            readOnly={readOnly}
          />
        </div>

        {/* Clinical Assessment */}
        <h3 className="font-semibold mt-4 mb-2">Clinical Assessment</h3>
        <div className="space-y-3">
          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Chief Complaints"
            value={chiefComplaints}
            onChange={(e) => setChiefComplaints(e.target.value)}
            readOnly={readOnly}
          />

          <div>
            <label className="font-semibold block mb-2">Physical Examination</label>
            <div className="space-y-3 p-3 bg-gray-50 rounded border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Region"
                  value={associatedSymptom.region}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, region: e.target.value })}
                  readOnly={readOnly}
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Inspection"
                  value={associatedSymptom.inspection}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, inspection: e.target.value })}
                  readOnly={readOnly}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Palpation"
                  value={associatedSymptom.palpation}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, palpation: e.target.value })}
                  readOnly={readOnly}
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Range of Motion"
                  value={associatedSymptom.rangeOfMotion}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, rangeOfMotion: e.target.value })}
                  readOnly={readOnly}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Muscle Strength"
                  value={associatedSymptom.muscleStrength}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, muscleStrength: e.target.value })}
                  readOnly={readOnly}
                />
                <textarea
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={2}
                  placeholder="Special Tests"
                  value={associatedSymptom.specialTests}
                  onChange={(e) => setAssociatedSymptom({ ...associatedSymptom, specialTests: e.target.value })}
                  readOnly={readOnly}
                />
              </div>
            </div>
          </div>

          <textarea
            className="w-full border p-2 rounded"
            rows={2}
            placeholder="Drug Allergy"
            value={drugAllergy}
            onChange={(e) => setDrugAllergy(e.target.value)}
            readOnly={readOnly}
          />
        </div>

        <h3 className="font-semibold mt-4 mb-2">Observations</h3>
        <textarea
          className="w-full border p-2 rounded"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          readOnly={readOnly}
          placeholder="Comments..."
        />

        {/* Medicines */}
        <h3 className="font-semibold mt-4 mb-2">Medicines</h3>
        {medicines.map((m, i) => (
          <div key={i} className="border p-3 rounded mb-2">
            <div className="flex items-center justify-between">
              <input
                type="text"
                className="border p-2 w-full rounded"
                placeholder="Medicine Name"
                value={m.name}
                onChange={(e) => {
                  const copy = [...medicines];
                  copy[i].name = e.target.value;
                  setMedicines(copy);
                }}
                readOnly={readOnly}
              />
              {!readOnly && (
                <button onClick={() => deleteMedicine(i)} className="ml-1 p-2 bg-red-600 text-white rounded-lg">
                  Delete
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 align-center mt-2">
              <div className="flex items-center gap-2">
                <label className="font-medium">Days:</label>
                <input
                  type="number"
                  min="1"
                  className="border p-1 rounded w-20"
                  value={m.days}
                  onChange={(e) => {
                    const copy = [...medicines];
                    copy[i].days = Number(e.target.value);
                    setMedicines(copy);
                  }}
                  readOnly={readOnly}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-medium">Dosage:</label>
                <input
                  type="text"
                  className="border p-1 rounded w-20"
                  value={m.dosage || ""}
                  onChange={(e) => {
                    const copy = [...medicines];
                    copy[i].dosage = e.target.value;
                    setMedicines(copy);
                  }}
                  readOnly={readOnly}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-medium">Route:</label>
                {readOnly ? (
                  <input type="text" className="border p-1 rounded w-32" value={(typeof m.route === 'object' ? m.route?.name : m.route) || ""} readOnly />
                ) : (
                  <Select
                    options={routes}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id.toString()}
                    placeholder="Select"
                    value={routes.find(r => r.name === (typeof m.route === 'object' ? m.route?.name : m.route)) || null}
                    onChange={(e) => {
                      if (e) {
                        const copy = [...medicines];
                        copy[i].route = e.name;
                        setMedicines(copy);
                      }
                    }}
                    className="w-40"
                  />
                )}
              </div>
            </div>

            {["morning", "afternoon", "evening", "night"].map((t) => (
              <div key={t} className="flex gap-4 items-center mt-2 ml-2">
                <label>
                  <input
                    type="checkbox"
                    checked={m[t]?.take || false}
                    onChange={() => toggle(i, t, "take")}
                    disabled={readOnly}
                  />{" "}
                  <span className="w-20 capitalize">{t}</span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={m[t]?.beforeFood || false}
                    onChange={() => toggle(i, t, "beforeFood")}
                    disabled={readOnly || !m[t]?.take}
                  />{" "}
                  Before food
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={m[t]?.afterFood || false}
                    onChange={() => toggle(i, t, "afterFood")}
                    disabled={readOnly || !m[t]?.take}
                  />{" "}
                  After food
                </label>
              </div>
            ))}
          </div>
        ))}

        {!readOnly && (
          <button onClick={addMedicine} className="mt-1 p-2 bg-blue-600 text-white rounded-lg">
            Add Medicine
          </button>
        )}

        <h3 className="font-semibold mt-4 mb-2">Plan & Advice</h3>
        <div className="space-y-3">
          <textarea className="w-full border p-2 rounded" rows={2} placeholder="Advice / Instructions" value={advice} onChange={e => setAdvice(e.target.value)} readOnly={readOnly} />
          <textarea className="w-full border p-2 rounded" rows={2} placeholder="Exercise Notes" value={exerciseNotes} onChange={e => setExerciseNotes(e.target.value)} readOnly={readOnly} />
        </div>

        <h3 className="font-semibold mt-4 mb-2">Request for Diagnosis</h3>
        <textarea
          className="w-full border p-2 rounded mb-3"
          rows={2}
          value={requestForDiagnosis}
          onChange={(e) => setRequestForDiagnosis(e.target.value)}
          readOnly={readOnly}
        />

        <h3 className="font-semibold mt-4 mb-2">Follow-up Date</h3>
        <input
          type="date"
          className="border p-2 rounded w-full mb-3"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          readOnly={readOnly}
        />

        <div className="flex justify-end gap-3 mt-4">
          <button className="px-4 py-2 bg-gray-300" onClick={onClose}>Close</button>
          <button className="px-4 py-2 bg-blue-600 text-white" onClick={downloadPdf}>
            Download PDF
          </button>
          {!readOnly && (
            <button className="px-4 py-2 bg-green-600 text-white" onClick={save}>
              Update
            </button>
          )}
        </div>

        {/* Hidden PDF content - 2 Pages */}
        <div
          ref={pdfRef}
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
          }}
        >
          {/* PAGE 1 */}
          <div
            id="report-page-1"
            className="bg-white text-black"
            style={{ width: "794px", minHeight: "1123px", padding: "0px" }} // A4 dimensions at 96 DPI
          >
            {/* Empty Header for Letterhead */}
            <div style={{ height: "160px" }}></div>

            <div className="px-12 pb-12">
              <div className="flex justify-between items-end border-b-2 border-gray-800 pb-2 mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 uppercase tracking-wide">Medical Report</h1>
                  <p className="text-gray-500 font-medium">Date: {new Date().toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-600">Report ID: #{report.id}</p>
                </div>
              </div>

              {/* Patient Details */}
              <section className="mb-8">
                <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Patient Profile</h2>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-2 gap-y-4 gap-x-8">
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase font-semibold">Name</span>
                    <span className="text-lg font-semibold text-gray-800">{patientName}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase font-semibold">Age / Gender</span>
                    <span className="text-lg font-semibold text-gray-800">{age} yrs / {gender}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase font-semibold">Occupation</span>
                    <span className="text-lg font-semibold text-gray-800">{occupation || "N/A"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase font-semibold">Height / Weight</span>
                    <span className="text-lg font-semibold text-gray-800">{height || "-"} / {weight || "-"}</span>
                  </div>
                </div>
              </section>

              {/* Clinical Assessment */}
              <section className="mb-8">
                <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Clinical Assessment</h2>

                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-1">Chief Complaints</h3>
                  <p className="p-3 bg-white border border-gray-200 rounded text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {chiefComplaints || "No complaints recorded."}
                  </p>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Physical Examination</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: "Region", val: associatedSymptom.region },
                      { label: "Inspection", val: associatedSymptom.inspection },
                      { label: "Palpation", val: associatedSymptom.palpation },
                      { label: "Range of Motion", val: associatedSymptom.rangeOfMotion },
                      { label: "Muscle Strength", val: associatedSymptom.muscleStrength },
                      { label: "Special Tests", val: associatedSymptom.specialTests }
                    ].map((item, idx) => (
                      <div key={idx} className="border border-gray-200 rounded p-2 bg-gray-50/50">
                        <span className="block text-xs font-bold text-gray-500 uppercase">{item.label}</span>
                        <span className="block text-sm text-gray-800 mt-1">{item.val || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-1">Drug Allergy</h3>
                  <p className="text-red-600 font-medium">{drugAllergy || "None"}</p>
                </div>
              </section>

              {/* Observations */}
              <section>
                <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Observations</h2>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded text-gray-800 whitespace-pre-wrap min-h-[100px]">
                  {comment || "No additional observations."}
                </div>
              </section>
            </div>
          </div>

          {/* PAGE 2 */}
          <div
            id="report-page-2"
            className="bg-white text-black mt-10" // mt-10 just to visually separate in DOM, doesn't affect capture
            style={{ width: "794px", minHeight: "1123px", padding: "0px" }}
          >
            {/* Empty Header for Letterhead */}
            <div style={{ height: "160px" }}></div>

            <div className="px-12 pb-12">
              {/* Medicines */}
              <section className="mb-8">
                <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Prescribed Medicines</h2>
                {medicines.length > 0 ? (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-blue-50 text-blue-900">
                        <th className="border border-blue-100 p-3 text-left w-1/4">Medicine Name</th>
                        <th className="border border-blue-100 p-3 text-left">Dosage & Route</th>
                        <th className="border border-blue-100 p-3 text-left">Duration</th>
                        <th className="border border-blue-100 p-3 text-left">Schedule</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicines.map((m, i) => (
                        <tr key={i} className="even:bg-gray-50">
                          <td className="border border-gray-200 p-3 font-bold text-gray-800">{m.name}</td>
                          <td className="border border-gray-200 p-3">
                            <div className="font-medium text-gray-700">{m.dosage || "-"}</div>
                            <div className="text-xs text-gray-500">{typeof m.route === 'object' ? m.route?.name : m.route}</div>
                          </td>
                          <td className="border border-gray-200 p-3">{m.days} days</td>
                          <td className="border border-gray-200 p-3">
                            <div className="flex gap-2 text-xs">
                              {["morning", "afternoon", "evening", "night"].map(t => {
                                if (!m[t]?.take) return null;
                                const timing = [];
                                if (m[t].beforeFood) timing.push("BF");
                                if (m[t].afterFood) timing.push("AF");
                                return (
                                  <div key={t} className="flex flex-col items-center bg-white border border-gray-200 p-1 rounded min-w-[50px]">
                                    <span className="font-bold text-gray-600 uppercase mb-0.5" style={{ fontSize: '10px' }}>{t.substr(0, 3)}</span>
                                    {timing.length > 0 ? (
                                      <span className="text-blue-600 font-bold">{timing.join("/")}</span>
                                    ) : (
                                      <span className="text-green-600 font-bold">✓</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-4 bg-gray-50 border border-dashed border-gray-300 rounded text-gray-500 text-center">
                    No medicines prescribed.
                  </div>
                )}
              </section>

              {/* Plan & Advice */}
              <section className="mb-8">
                <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Plan & Advice</h2>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <h3 className="font-bold text-gray-700 mb-2">General Advice / Instructions</h3>
                    <div className="p-4 bg-yellow-50 border border-yellow-100 rounded text-gray-800 min-h-[80px] whitespace-pre-wrap">
                      {advice || "—"}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-700 mb-2">Exercise / Physiotherapy Notes</h3>
                    <div className="p-4 bg-green-50 border border-green-100 rounded text-gray-800 min-h-[80px] whitespace-pre-wrap">
                      {exerciseNotes || "—"}
                    </div>
                  </div>
                </div>
              </section>

              <div className="grid grid-cols-2 gap-8 mb-8">
                {/* Diagnosis */}
                <section>
                  <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Diagnosis Request</h2>
                  <div className="p-3 bg-white border border-gray-200 rounded text-gray-800 min-h-[60px]">
                    {requestForDiagnosis || "None"}
                  </div>
                </section>

                {/* Follow Up */}
                <section>
                  <h2 className="text-lg font-bold text-blue-900 uppercase border-b border-blue-200 mb-4 pb-1">Next Visit</h2>
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded text-center">
                    {followUpDate ? (
                      <div className="flex flex-col items-center">
                        <span className="text-sm text-blue-600 uppercase font-bold">Planned Date</span>
                        <span className="text-xl font-bold text-blue-900">{new Date(followUpDate).toLocaleDateString()}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500">Not scheduled</span>
                    )}
                  </div>
                </section>
              </div>

              <div className="mt-12 text-center">
                <p className="text-gray-400 italic text-sm">Thank you for visiting.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}